// This file is imported by module-federation/init.ts which is imported
// by the early entry. Be mindful of the import weight when adding to this file.

function isOptionalParam(segment) {
  return segment.startsWith(':') && segment.endsWith('?');
}

// Matches any wildcard segment syntax
function isWildcardSegment(segment) {
  return segment === '*' || segment === '*?' || segment === '{*}?';
}

// Optional wildcards (*?, {*}?) match zero or more trailing segments.
// A required wildcard (*) requires at least one trailing segment (the slash).
function isOptionalWildcard(segment) {
  return segment === '*?' || segment === '{*}?';
}
export function replacePathParams(targetPath, sourcePath, appendRemainingSegments = true) {
  if (targetPath === '/') {
    return targetPath;
  }
  const targetSegments = targetPath.split('/');
  const sourceSegments = sourcePath.split('/');
  const final = [];
  sourceSegments.forEach((sourceSegment, index) => {
    const targetSegment = targetSegments === null || targetSegments === void 0 ? void 0 : targetSegments[index];
    // if there are no more segments in the target path and appendRemainingSegments is
    // NOT set, then we are done
    if (index >= targetSegments.length && !appendRemainingSegments) {
      return;
    }
    // if appending is enabled and theres no target segments left, append everything from source
    else if (index >= targetSegments.length && appendRemainingSegments) {
      final.push(sourceSegment);
    }
    // if the target segment is a path param, replace it with the source segment
    else if (targetSegment.startsWith(':')) {
      final.push(sourceSegment);
    }
    // wildcard segment — use the actual source value, not the pattern
    else if (isWildcardSegment(targetSegment)) {
      final.push(sourceSegment);
    }
    // otherwise, target and source are equal, copy one over
    else {
      final.push(targetSegment);
    }
  });
  return final.join('/');
}
function matchRouteSegments(routePattern, pathname) {
  if (routePattern === '/') {
    return true;
  }
  const routeSegments = routePattern.split('/');
  const pathSegments = pathname.split('/');
  const lastIndex = routeSegments.length - 1;
  for (let i = 0; i < routeSegments.length; i++) {
    const seg = routeSegments[i];

    // Validate: wildcards and optional params are only valid as the last segment
    if (isWildcardSegment(seg) && i !== lastIndex) {
      throw new Error(`Invalid route pattern "${routePattern}": wildcard (*) is only allowed as the last segment`);
    }
    if (isOptionalParam(seg) && i !== lastIndex) {
      throw new Error(`Invalid route pattern "${routePattern}": optional param (${seg}) is only allowed as the last segment`);
    }

    // Path has fewer segments than the route pattern — only match if all
    // remaining route segments are optional (optional wildcards or optional params)
    if (i >= pathSegments.length) {
      const remaining = routeSegments.slice(i);
      return remaining.every(s => isOptionalWildcard(s) || isOptionalParam(s));
    }

    // Trailing wildcard matches all remaining path segments
    if (isWildcardSegment(seg)) {
      return true;
    }

    // Optional params match any value (same as named params, but handled
    // above when the path is too short)
    if (isOptionalParam(seg)) {
      return true;
    }

    // Named parameter segments (e.g. :portalId) match any value
    if (seg.startsWith(':')) {
      continue;
    }

    // Literal segments must match exactly
    if (seg !== pathSegments[i]) {
      return false;
    }
  }

  // All route segments matched; path may have additional trailing segments (prefix match)
  return true;
}
function getMatchingRoutes(routes, pathname) {
  return routes.filter(route => matchRouteSegments(route, pathname));
}
function hasWildcard(route) {
  var _route$split$pop;
  const lastSegment = (_route$split$pop = route.split('/').pop()) !== null && _route$split$pop !== void 0 ? _route$split$pop : '';
  return isWildcardSegment(lastSegment);
}
export function findMostSpecificRoute(routes, pathname) {
  const matches = getMatchingRoutes(routes, pathname);
  if (matches.length === 0) return undefined;
  return matches.reduce((best, route) => {
    const routeLen = route.split('/').length;
    const bestLen = best.split('/').length;

    // Prefer more segments (more specific match)
    if (routeLen !== bestLen) {
      return routeLen > bestLen ? route : best;
    }

    // At equal segment count, prefer non-wildcard routes over wildcard routes
    if (hasWildcard(best) && !hasWildcard(route)) {
      return route;
    }
    return best;
  });
}
export function findLeastSpecificRoute(routes, pathname) {
  const matches = getMatchingRoutes(routes, pathname);
  if (matches.length === 0) return undefined;
  return matches.reduce((best, route) => route.split('/').length < best.split('/').length ? route : best);
}